import { Button, Card, Switch, Tag, Tree } from "@arco-design/web-react";
import { Pencil, Plus, UserCog, UserMinus, UserPlus, UserRound, UsersRound } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import type { SysDeptStaffTreeItem, SysDeptTreeNode } from "../types";
import "./SysDeptUserPage.css";

type SysDeptTreeDataItem = {
  key: string;
  title: string;
  nodeType: "dept" | "staff";
  dept?: SysDeptTreeNode;
  staff?: SysDeptStaffTreeItem;
  parentDept?: Pick<SysDeptTreeNode, "id" | "deptName">;
  children?: SysDeptTreeDataItem[];
  disabled?: boolean;
};

type SysDeptTreePanelProps = {
  canManage: boolean;
  items: SysDeptTreeNode[];
  selectedDeptId?: number;
  includeDisabled: boolean;
  onIncludeDisabledChange: (includeDisabled: boolean) => void;
  onSelectDept: (deptId: number) => void;
  onCreateDept: (parentId?: number) => void;
  onEditDept: (item: SysDeptTreeNode) => void;
  onEditStaff: (userId: number) => void;
  onEditStaffSupervisors: (userId: number) => void;
  onCreateStaff: (deptId: number) => void;
  onAddExistingStaff: (deptId: number) => void;
  onRemoveStaffFromDept: (userId: number, deptId: number) => void;
};

/** 部门树面板。 */
export function SysDeptTreePanel({
  canManage,
  items,
  selectedDeptId,
  includeDisabled,
  onIncludeDisabledChange,
  onSelectDept,
  onCreateDept,
  onEditDept,
  onEditStaff,
  onEditStaffSupervisors,
  onCreateStaff,
  onAddExistingStaff,
  onRemoveStaffFromDept,
}: SysDeptTreePanelProps) {
  const treeData = useMemo(() => mapDeptTreeData(items), [items]);
  const defaultExpandedKeys = useMemo(() => collectDeptTreeKeys(treeData), [treeData]);
  const treeInstanceKey = defaultExpandedKeys.join("|");

  return (
    <Card className="sys-dept-user-card" bodyStyle={{ padding: 18 }}>
      <div className="sys-dept-user-tree-header">
        <h2>组织结构</h2>
        <div className="sys-dept-user-tree-header-actions">
          <label className="sys-dept-user-status-switch">
            <span>{includeDisabled ? "全部状态" : "已启用"}</span>
            <Switch
              aria-label="部门状态过滤"
              checked={includeDisabled}
              onChange={onIncludeDisabledChange}
            />
          </label>
          {canManage ? (
            <Button shape="round" icon={<Plus size={16} />} onClick={() => onCreateDept(undefined)}>
              新增部门
            </Button>
          ) : null}
        </div>
      </div>
      {items.length === 0 ? <div className="sys-dept-user-empty">暂无部门数据</div> : null}
      <Tree
        animation={false}
        blockNode
        key={treeInstanceKey}
        showLine
        treeData={treeData}
        selectedKeys={selectedDeptId ? [`dept-${selectedDeptId}`] : []}
        defaultExpandedKeys={defaultExpandedKeys}
        onSelect={(keys) => {
          const key = keys[0];
          if (!key?.startsWith("dept-")) {
            return;
          }
          onSelectDept(Number(key.replace("dept-", "")));
        }}
        renderTitle={(node) => renderTreeTitle(node as SysDeptTreeDataItem, onSelectDept)}
        renderExtra={(node) =>
          canManage ? renderTreeExtra(node as SysDeptTreeDataItem, {
            onAddExistingStaff,
            onCreateDept,
            onCreateStaff,
            onEditDept,
            onEditStaff,
            onEditStaffSupervisors,
            onRemoveStaffFromDept,
          }) : null
        }
      />
    </Card>
  );
}

/** 转换为 Arco Tree 可消费的数据。 */
function mapDeptTreeData(items: SysDeptTreeNode[]): SysDeptTreeDataItem[] {
  return items.map((item) => ({
    key: `dept-${item.id}`,
    title: item.deptName,
    nodeType: "dept",
    dept: item,
    children: [
      ...item.children.map((child) => mapDeptTreeData([child])[0]),
      ...item.staffItems.map((staff) => ({
        key: `staff-${item.id}-${staff.userId}`,
        title: staff.displayName,
        nodeType: "staff" as const,
        staff,
        parentDept: { id: item.id, deptName: item.deptName },
        disabled: true,
      })),
    ],
  }));
}

/** 收集默认展开的部门节点。 */
function collectDeptTreeKeys(items: SysDeptTreeDataItem[]): string[] {
  return items.flatMap((item) => [
    item.key,
    ...(item.children ? collectDeptTreeKeys(item.children) : []),
  ]);
}

/** 渲染部门和员工节点标题。 */
function renderTreeTitle(
  node: SysDeptTreeDataItem,
  onSelectDept: (deptId: number) => void,
): ReactNode {
  if (node.nodeType === "staff" && node.staff) {
    return (
      <span className="sys-dept-user-tree-title is-staff">
        <UserRound size={14} />
        <span>{node.staff.displayName}</span>
        <span className="sys-dept-user-tree-meta">{node.staff.username}</span>
      </span>
    );
  }
  if (!node.dept) {
    return node.title;
  }
  return (
    <span
      aria-label={`选择部门${node.dept.deptName}`}
      className="sys-dept-user-tree-title sys-dept-user-tree-title-button"
      data-testid={`sys-dept-tree-dept-${node.dept.id}`}
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        onSelectDept(node.dept.id);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          onSelectDept(node.dept.id);
        }
      }}
    >
      <span>{node.dept.deptName}</span>
      <span className="sys-dept-user-tree-meta">{node.dept.userCount} 人</span>
      <Tag color={node.dept.status === 1 ? "green" : "orangered"}>{node.dept.statusLabel}</Tag>
    </span>
  );
}

/** 渲染部门节点操作区。 */
function renderTreeExtra(
  node: SysDeptTreeDataItem,
  actions: {
    onAddExistingStaff: (deptId: number) => void;
    onCreateDept: (parentId?: number) => void;
    onCreateStaff: (deptId: number) => void;
    onEditDept: (item: SysDeptTreeNode) => void;
    onEditStaff: (userId: number) => void;
    onEditStaffSupervisors: (userId: number) => void;
    onRemoveStaffFromDept: (userId: number, deptId: number) => void;
  },
): ReactNode {
  if (node.nodeType === "staff" && node.staff && node.parentDept) {
    const staff = node.staff;
    const dept = node.parentDept;
    return (
      <span className="sys-dept-user-tree-actions is-staff-actions" onClick={(event) => event.stopPropagation()}>
        <Button
          type="text"
          shape="circle"
          aria-label={`编辑员工 ${staff.displayName}`}
          icon={<Pencil size={14} />}
          onClick={() => actions.onEditStaff(staff.userId)}
        />
        <Button
          type="text"
          shape="circle"
          aria-label={`添加领导 ${staff.displayName}`}
          icon={<UserCog size={14} />}
          onClick={() => actions.onEditStaffSupervisors(staff.userId)}
        />
        <Button
          type="text"
          shape="circle"
          aria-label={`从${dept.deptName}移出${staff.displayName}`}
          icon={<UserMinus size={14} />}
          onClick={() => actions.onRemoveStaffFromDept(staff.userId, dept.id)}
        />
      </span>
    );
  }
  if (node.nodeType !== "dept" || !node.dept) {
    return null;
  }
  const dept = node.dept;
  return (
    <span className="sys-dept-user-tree-actions" onClick={(event) => event.stopPropagation()}>
      <Button
        type="text"
        shape="circle"
        aria-label={`新增员工到${dept.deptName}`}
        icon={<UserPlus size={15} />}
        onClick={() => actions.onCreateStaff(dept.id)}
      />
      <Button
        type="text"
        shape="circle"
        aria-label={`添加已有员工到${dept.deptName}`}
        icon={<UsersRound size={15} />}
        onClick={() => actions.onAddExistingStaff(dept.id)}
      />
      <Button
        type="text"
        shape="circle"
        aria-label={`编辑部门 ${dept.deptName}`}
        icon={<Pencil size={15} />}
        onClick={() => actions.onEditDept(dept)}
      />
      <Button
        type="text"
        aria-label={`新增子部门到${dept.deptName}`}
        onClick={() => actions.onCreateDept(dept.id)}
      >
        新增子部门
      </Button>
    </span>
  );
}
